import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

export interface GhostContext { mailboxId?: string; messageId?: string; to?: string; subject?: string }

const warmed = new Set<string>()

/** Tidy a raw model continuation against the draft: no echo, no quotes, right spacing. */
function clean(raw: string, draft: string) {
  let text = raw.replace(/^["“]/, '')
  const tail = draft.slice(-24).trimStart()
  if (tail && text.trimStart().startsWith(tail)) text = text.trimStart().slice(tail.length)
  if (/[\s(]$/.test(draft)) text = text.trimStart()
  else if (text && !/^[\s.,!?;:'’)\]-]/.test(text) && /\w$/.test(draft)) text = ` ${text}`
  return text.replace(/\n+$/, '').slice(0, 200)
}
let available = false
/** Set once from /api/session: whether the server can autocomplete. */
export const setAutocomplete = (on: boolean) => { available = on }

/**
 * A textarea with Tab autocomplete in the writer's own voice. The suggestion is
 * drawn as faint text after the caret by a mirror layer; Tab accepts it, Esc
 * dismisses it, and typing the same characters keeps it going.
 */
export const GhostTextarea = forwardRef<HTMLTextAreaElement, {
  value: string; onChange: (value: string) => void; context: GhostContext; enabled?: boolean
  className: string; placeholder?: string; rows?: number
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
}>(function GhostTextarea({ value, onChange, context, enabled: wanted = true, className, placeholder, rows, onKeyDown }, outer) {
  const enabled = wanted && available
  const area = useRef<HTMLTextAreaElement>(null)
  const mirror = useRef<HTMLDivElement>(null)
  useImperativeHandle(outer, () => area.current!)
  const [ghost, setGhost] = useState('')
  const [atEnd, setAtEnd] = useState(true)
  const contextRef = useRef(context)
  contextRef.current = context
  const draftRef = useRef(value)
  draftRef.current = value
  const skip = useRef(false)

  // Warm the server's caches (voice samples, the message being answered) before the first keystroke.
  const warmKey = `${context.mailboxId ?? ''}:${context.messageId ?? ''}`
  useEffect(() => {
    if (!enabled || warmed.has(warmKey)) return
    warmed.add(warmKey)
    void fetch('/api/complete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ warm: true, ...contextRef.current }) }).catch(() => {})
  }, [enabled, warmKey])

  // Ask for a continuation once typing pauses, only with the caret at the end.
  useEffect(() => {
    if (!enabled) return
    if (skip.current) { skip.current = false; return }
    setGhost('')
    if (!atEnd || value.trim().length < 3) return
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const response = await fetch('/api/complete', {
          method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ draft: value, ...contextRef.current }),
        })
        // Show words as they stream in.
        const reader = response.body!.pipeThrough(new TextDecoderStream()).getReader()
        let raw = ''
        for (;;) {
          const { value, done } = await reader.read()
          if (done || controller.signal.aborted) break
          raw += value
          setGhost(clean(raw, draftRef.current))
        }
        if (!controller.signal.aborted) setGhost(clean(raw.replace(/["”]\s*$/, ''), draftRef.current))
      } catch { /* aborted or offline */ }
    }, 150)
    return () => { clearTimeout(timer); controller.abort() }
  }, [value, atEnd, enabled])

  const syncScroll = () => { if (mirror.current && area.current) mirror.current.scrollTop = area.current.scrollTop }
  useEffect(syncScroll, [ghost, value])
  const caret = () => { const el = area.current; if (el) setAtEnd(el.selectionStart === el.value.length && el.selectionEnd === el.value.length) }

  return (
    <div className="ghost-wrap">
      <textarea ref={area} className={className} rows={rows} placeholder={placeholder} value={value}
        onScroll={syncScroll} onSelect={caret} onBlur={() => setGhost('')}
        onChange={event => {
          const next = event.target.value
          // Typed what the suggestion said: keep the rest instead of refetching.
          if (ghost && next.startsWith(value) && next.length > value.length && ghost.startsWith(next.slice(value.length))) {
            const rest = ghost.slice(next.length - value.length)
            skip.current = !!rest
            setGhost(rest)
          }
          onChange(next)
        }}
        onKeyDown={event => {
          if (ghost && event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault(); event.stopPropagation()
            skip.current = true
            onChange(value + ghost); setGhost('')
            return
          }
          if (ghost && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setGhost(''); return }
          onKeyDown?.(event)
        }} />
      {ghost && atEnd && (
        <div ref={mirror} className={`${className} ghost-mirror`} aria-hidden="true">
          <span className="ghost-typed">{value}</span><span className="ghost-text">{ghost}</span>
          {!/\n/.test(ghost) && <span className="ghost-key">tab</span>}
        </div>
      )}
    </div>
  )
})
